// hooks/useFilePreview.ts
import { useCallback, useState } from "react";

export type FileType = "pdf" | "image" | "xlsx" | "doc" | "unknown";

export interface FilePreviewData {
  blobUrl: string;
  type: FileType;
  fileName: string;
  rawArrayBuffer?: ArrayBuffer;
}

const getFileType = (fileName: string): FileType => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (!ext) return "unknown";
  if (ext === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "image";
  if (["xlsx", "xls", "csv"].includes(ext)) return "xlsx";
  if (["doc", "docx"].includes(ext)) return "doc";
  return "unknown";
};

const getFileTypeFromMime = (mime: string): FileType => {
  if (mime.includes("pdf")) return "pdf";
  if (mime.includes("image")) return "image";
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime.includes("csv")) return "xlsx";
  if (mime.includes("word") || mime.includes("document")) return "doc";
  return "unknown";
};

export const useFilePreview = () => {
  const [previewData, setPreviewData] = useState<FilePreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // hooks/useFilePreview.ts
  const openPreview = async (downloadUrl: string, fileName: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(downloadUrl);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();

      const type = getFileType(fileName);

      const mimeMap: Record<FileType, string> = {
        pdf:     "application/pdf",
        image:   "image/png",
        xlsx:    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        doc:     "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        unknown: "application/octet-stream",
      };

      // ✅ Force correct MIME type — ignore what GCS returns
      const blob = new Blob([arrayBuffer], { type: mimeMap[type] });
      const blobUrl = URL.createObjectURL(blob);

      setPreviewData({ blobUrl, type, fileName, rawArrayBuffer: arrayBuffer });

    } catch (err: any) {
      console.error("🔴 Preview error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const closePreview = useCallback(() => {
    // ✅ Revoke blob URL to free memory
    if (previewData?.blobUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewData.blobUrl);
    }
    // ✅ Full reset — ensures next openPreview starts fresh
    setPreviewData(null);
    setError(null);
    setLoading(false);
  }, [previewData]);

  return { previewData, loading, error, openPreview, closePreview };
};